/**
 * useGA — Google Analytics 4 page-view tracker for the SPA.
 *
 * The gtag.js snippet in public/index.html sends the FIRST page_view
 * automatically (via `send_page_view: true` in the config call), but
 * React Router client-side navigations DON'T trigger a full page load,
 * so subsequent routes never reach GA on their own.
 *
 * This hook watches react-router's `useLocation()` and fires a
 * synthetic `page_view` event on every path change. Runs no-op if
 * gtag isn't loaded (e.g. ad-blockers, offline dev, error).
 *
 * Mount ONCE inside the top-level <BrowserRouter> in App.js.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Measurement ID from public/index.html — keep in sync if it ever
// changes so a broken config in index.html doesn't silently orphan
// SPA page_view events.
const GA_ID = 'G-C5EK023QVJ';

const useGA = () => {
  const location = useLocation();
  useEffect(() => {
    // gtag may be missing in dev, blocked by an ad-blocker, or not
    // yet loaded on the first render. Silently no-op in those cases.
    const gtag = window.gtag || window.__fdaGtag;
    if (typeof gtag !== 'function') return;
    try {
      gtag('event', 'page_view', {
        page_path:     location.pathname + location.search,
        page_location: window.location.href,
        page_title:    document.title,
        send_to:       GA_ID,
      });
    } catch { /* never crash the SPA over analytics */ }
  }, [location.pathname, location.search]);
};

export default useGA;
